import { useState, useEffect, useRef, useContext } from 'react';
import { Alert, Avatar, Chip, Divider, IconButton, Menu, MenuItem, Snackbar, Tooltip } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
// Our Components
import { AuthContext } from 'context/auth-context';
import { CustomTableContainer } from 'components/TransactionsSection/table/CustomTableContainer';
import { HeaderTableCell } from 'components/TransactionsSection/table/HeaderTableCell';
import { MainComponent as AddBankAccount } from './addbankaccount/MainComponent';
import { ManualBankLink } from '../ManualBankLink';
import { Sophtron } from './banklinking/Sophtron';
import { TitleTable } from 'components/TransactionsSection/table/TitleTable';
import { useStyles } from 'components/UsersSection/table/styles';
import ConfirmationModal from 'components/shared/ConfirmationModal';
// Services
import { Banks } from 'lib/api';
// Utils and Helpers
import { formatDate } from 'helpers/formatDate';
import { hasPINModel } from 'helpers/businesses';
import { isAdminUser } from 'helpers/auth';
// Assets
import { ReactComponent as NoBankAccount } from 'assets/overviewSection/NoBankAccount.svg';
import PaystandLogo from 'assets/overviewSection/LogoWithoutText.svg';

const columnsCreditModel = [
    { id: 'accountId', label: 'BANK ACCOUNT', maxWidth: 80, align: 'left' },
    { id: 'type', label: 'TYPE', maxHeight: 40, align: 'LEFT' }, //was code
    {
        id: 'dateAdded', // was population
        label: 'DATE ADDED',
        minWidth: 40,
        align: 'left',
        format: value => value.toLocaleString('en-US')
    },
    {
        id: 'status',
        label: 'STATUS',
        minWidth: 100,
        align: 'left'
    },
    {
        id: 'action',
        label: 'ACTION',
        minWidth: 150,
        align: 'center'
    }
];

const columnsDepositModel = [
    { id: 'accountId', label: 'BANK ACCOUNT', maxWidth: 80, align: 'left' },
    { id: 'type', label: 'TYPE', maxHeight: 40, align: 'LEFT' }, //was code
    {
        id: 'dateAdded', // was population
        label: 'DATE ADDED',
        minWidth: 40,
        align: 'left',
        format: value => value.toLocaleString('en-US')
    },
    {
        id: 'status',
        label: 'STATUS',
        minWidth: 100,
        align: 'left'
    }
];

export default function StickyHeadTable() {
    const classes = useStyles();
    const isMounted = useRef();
    const [bankAccounts, setBankAccounts] = useState();
    const [isDepositFunding, setIsDepositFunding] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [addBank, setAddBank] = useState(false);
    const [openManualLink, setOpenManualLink] = useState(false);
    const [openBankSettings, setOpenBankSettings] = useState(false);
    const [isPrimaryLoading, setIsPrimaryLoading] = useState(false);
    const [currentAccount, setCurrentAccount] = useState(null);
    const { userInfo, setUserInfo, businessInfo } = useContext(AuthContext);
    const [deleteBank, setDeleteBank] = useState(false);
    const [bankAccountID, setBankAccountID] = useState(null);
    const [shown, setShown] = useState(false);
    const [updatePrimary, setUpdatePrimary] = useState(false);
    const [modalConfirm, setModalConfirm] = useState({
        action: '',
        openModal: false,
        component: '',
        cb: ''
    });
    const anchorMenu = useRef();
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);

    useEffect(() => {
        isMounted.current = true;
        if (userInfo.kyb) {
            fetchData();
        }
        return () => {
            isMounted.current = false;
        };
    }, [userInfo]);

    const fetchData = () => {
        Banks.getTheBanks().then(response => {
            if (!isMounted.current) return;
            setBankAccounts(response.rows);
        });
    };

    useEffect(() => {
        if (businessInfo.is_deposit_funding) {
            setIsDepositFunding(true);
        }
    }, [businessInfo]);

    useEffect(() => {
        if (deleteBank) {
            setIsPrimaryLoading(true);
            handleCloseMenu();
            Banks.apiDeleteBankAccount(bankAccountID)
                .then(() => {
                    Banks.getTheBanks().then(response => {
                        setBankAccounts(response.rows);
                        setIsPrimaryLoading(false);
                        setDeleteBank(false);
                        setModalConfirm(false);
                    });
                })
                .catch(err => {
                    console.error(err);
                    setIsPrimaryLoading(false);
                    setDeleteBank(false);
                });
        }
    }, [bankAccountID, deleteBank]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const closeModal = () => {
        setAddBank(!addBank);
    };

    const onComplete = () => {
        setUserInfo(uInfo => ({
            ...uInfo,
            bank_pending_add: false,
            reconnect: false
        }));
        fetchData();
    };

    const splitText = text => {
        const chartUpper = text.split('').filter(chart => /[A-Z]/.test(chart));
        const indexUpper = text.indexOf(chartUpper);
        const firstWord = text.slice(0, indexUpper);
        const secondWord = text.slice(indexUpper);
        return firstWord + ' ' + secondWord;
    };

    const statusFormat = row => {
        if (row.needs_relink) {
            return (
                <Chip
                    label="Disconnected"
                    sx={{
                        bgcolor: '#FBD8E3 !important',
                        color: '#F65570'
                    }}
                />
            );
        }
        if (row.is_disabled) {
            return (
                <Chip
                    label="Inactive"
                    sx={{
                        bgcolor: '#FBD8E3 !important',
                        color: '#F65570'
                    }}
                />
            );
        }
        return (
            <Chip
                label="Connected"
                sx={{
                    bgcolor: '#E2EFD8',
                    color: '#77B44B'
                }}
            />
        );
    };

    const handleCardMenuClick = (event, account) => {
        if (account.is_primary) return setIsSnackbarOpen(true);
        setOpenBankSettings(true);
        setCurrentAccount(account);
        anchorMenu.current = event.currentTarget;
    };

    const handleCloseMenu = event => {
        setOpenBankSettings(false);
        anchorMenu.current = null;
    };

    const settingButtonEnabled = row => {
        return !!row.needs_relink || !row.is_primary || bankAccounts?.length > 1;
    };

    useEffect(() => {
        if (updatePrimary) {
            handleCloseMenu();
            setIsPrimaryLoading(true);
            Banks.makePrimary(bankAccountID)
                .then(response => {
                    setCurrentAccount(response);
                    setIsPrimaryLoading(false);
                    Banks.getTheBanks().then(response => {
                        handleCloseMenu();
                        setModalConfirm(false);
                        setUpdatePrimary(false);
                        setBankAccounts(response.rows);
                    });
                })
                .catch(() => {
                    setUpdatePrimary(false);
                    setIsPrimaryLoading(false);
                });
        }
    }, [bankAccountID, updatePrimary]);

    const deleteBankAccount = id => {
        setBankAccountID(id);
        setModalConfirm({
            action: 'delete',
            openModal: true,
            component: 'bank',
            cb: () => setDeleteBank(true)
        });
    };

    const makePrimaryBankAccount = id => {
        setBankAccountID(id);
        setModalConfirm({
            action: 'make primary',
            openModal: true,
            component: 'bank',
            cb: () => setUpdatePrimary(true)
        });
    };

    return (
        <CustomTableContainer height="20em">
            <Stack spacing={2} direction="row" marginBottom="1rem">
                <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                    <TitleTable title="Funding Accounts" />
                </Typography>

                {userInfo.kyb && isAdminUser(userInfo?.role?.id) && businessInfo.permissions?.addAdditionalBanks && (
                    <Button variant="outlined" onClick={() => setShown(!shown)}>
                        Add Account
                    </Button>
                )}
            </Stack>

            <ManualBankLink openManualLink={openManualLink} setOpenManualLink={setOpenManualLink} fetchData={fetchData} />

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 440 }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead sx={{ position: 'relative', zIndex: 1 }}>
                            {!isDepositFunding ? (
                                <TableRow>
                                    {columnsCreditModel.map((column, index) => (
                                        <HeaderTableCell
                                            key={column.id}
                                            value={column.label}
                                            index={index}
                                            position={bankAccounts?.length > 0 ? column.align : 'center'}
                                        />
                                    ))}
                                </TableRow>
                            ) : (
                                <TableRow>
                                    {columnsDepositModel.map((column, index) => (
                                        <HeaderTableCell
                                            key={column.id}
                                            value={column.label}
                                            index={index}
                                            position={bankAccounts?.length > 0 ? column.align : 'center'}
                                        />
                                    ))}
                                </TableRow>
                            )}
                        </TableHead>
                        <TableBody
                            sx={{
                                height: (!bankAccounts || bankAccounts?.length === 0) && '177px'
                            }}>
                            {!!bankAccounts?.length &&
                                bankAccounts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => {
                                    return (
                                        <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                                            <TableCell align={row.align}>
                                                <label className={classes.textRowPrimary}>
                                                    {hasPINModel(businessInfo) && (
                                                        <img
                                                            src={PaystandLogo}
                                                            alt={'paystand-logo'}
                                                            style={{
                                                                marginRight: '10px',
                                                                position: 'relative',
                                                                top: '4px'
                                                            }}
                                                        />
                                                    )}
                                                    {row.bank_name}{' '}
                                                    {!!row.is_primary && (
                                                        <Chip
                                                            label="P"
                                                            size="small"
                                                            sx={{
                                                                height: '16px',
                                                                bgcolor: '#E2EFD8',
                                                                color: '#77B44B'
                                                            }}
                                                        />
                                                    )}
                                                    {!hasPINModel(businessInfo) && (
                                                        <>
                                                            <br />
                                                            <label>{row.name}</label>
                                                        </>
                                                    )}
                                                </label>
                                            </TableCell>
                                            <TableCell align={row.align} style={{ width: row.minWidth }}>
                                                <label className={classes.textRowPrimary} style={{ textTransform: 'capitalize' }}>
                                                    {splitText(row.account_type)}
                                                </label>
                                            </TableCell>
                                            <TableCell align={row.align} style={{ width: row.minWidth }}>
                                                <label className={classes.textRowPrimary}>{formatDate(row.created_at)}</label>
                                            </TableCell>
                                            <TableCell align={row.align} style={{ width: row.minWidth }}>
                                                {statusFormat(row)}
                                            </TableCell>
                                            {!isDepositFunding && (
                                                <TableCell align={row.align} style={{ width: row.minWidth }}>
                                                    {settingButtonEnabled(row) && (
                                                        <Tooltip title={row.is_primary ? '' : 'Bank settings'}>
                                                            <IconButton
                                                                size="small"
                                                                sx={{ ml: 2 }}
                                                                onClick={e => handleCardMenuClick(e, row)}>
                                                                <Avatar
                                                                    sx={{
                                                                        width: 32,
                                                                        height: 32,
                                                                        bgcolor: '#FFFFFF',
                                                                        borderColor: '#A8B6C1',
                                                                        color: '#A8B6C1'
                                                                    }}>
                                                                    <MoreVert />
                                                                </Avatar>
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            {(!bankAccounts || bankAccounts?.length === 0) && (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ textAlign: 'center' }}>
                                        <NoBankAccount />
                                        <br />
                                        <label
                                            style={{
                                                fontFamily: 'Montserrat',
                                                fontStyle: 'normal',
                                                fontWeight: 500,
                                                fontSize: '13px',
                                                lineHeight: '16px',
                                                color: '#A8B6C1'
                                            }}>
                                            You do not have a bank account linked yet
                                        </label>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {bankAccounts?.length > 0 && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 100]}
                        component="div"
                        count={bankAccounts ? bankAccounts.length : 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                )}
            </Paper>
            <AddBankAccount closeModal={closeModal} openModal={addBank} />
            <Menu anchorEl={anchorMenu.current} open={openBankSettings} onClose={handleCloseMenu}>
                {/*!!currentAccount?.needs_relink && (
          <MenuItem onClick={handleRelinkItem} disabled={isSynapseLoading}>
            {isSynapseLoading ? <CircularProgress /> : "Reconnect"}
          </MenuItem>
        )*/}
                {!!currentAccount?.needs_relink && <Divider sx={{ my: 0.5 }} />}
                {bankAccounts?.length > 1 && <MenuItem onClick={() => deleteBankAccount(currentAccount?.id)}>Remove</MenuItem>}
                {!currentAccount?.is_primary && <Divider sx={{ my: 0.5 }} />}
                {!currentAccount?.is_primary && (
                    <MenuItem onClick={() => makePrimaryBankAccount(currentAccount?.id)} disabled={isPrimaryLoading}>
                        Make Primary
                    </MenuItem>
                )}
            </Menu>
            <ConfirmationModal
                open={modalConfirm.openModal}
                close={() => setModalConfirm(false)}
                action={modalConfirm.action}
                component={modalConfirm.component}
                loader={isPrimaryLoading}
                nextFuntion={modalConfirm.cb}
            />
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={6000}
                open={isSnackbarOpen}
                onClose={() => setIsSnackbarOpen(false)}>
                <Alert severity="warning" variant="filled">
                    You can not modify primary bank account
                </Alert>
            </Snackbar>
            {shown ? <Sophtron shown={shown} setShown={setShown} onComplete={onComplete} /> : null}
        </CustomTableContainer>
    );
}
